import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { languageSelector, setLanguage } from '@store/slices/common.slice';
import { setRoleSwitchDialogVisible } from '@store/slices/portal.slice';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, LanguageCode, PortalRoutePath } from '@models';
import { authService, userPreferencesService } from '@services';
import { useLoggedinUser } from '@hooks';
import { RxUtils, Tr } from '@utils';
import { Avatar, Box, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { OutlinedIconButton } from '@components/common';
import { LogoutIcon, OrganizationsIcon, SettingsIcon } from '@components/icons';
import { RoleSwitchDialog } from '../RoleSwitchDialog';
import { GuardedRender } from '../GuardedRender';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

interface IUserActionsProps {
	sidebarCollapsed?: boolean;
}

export const UserActions: FC<IUserActionsProps> = ({ sidebarCollapsed }) => {
	const loggedInUser = useLoggedinUser();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [languageSelectorButton, setLanguageSelectorButton] = useState<HTMLElement>();
	const selectedLanguageCode = useSelector(languageSelector);
	const selectedLanguage = AVAILABLE_LANGUAGES.find(({ code }) => selectedLanguageCode === code) || DEFAULT_LANGUAGE;

	const closeLanguageSelectionMenu = () => setLanguageSelectorButton(undefined);
	const onLanguageSelect = (language: LanguageCode) => {
		loggedInUser?.isAdmin() ? dispatch(setLanguage(language)) : RxUtils.promisify(userPreferencesService.update({ language }));
		closeLanguageSelectionMenu();
	};

	return !sidebarCollapsed ? (
		<Stack direction="row" spacing={1} sx={{ paddingTop: 1, alignItems: 'center' }}>
			<Box>
				<Avatar
					data-testid="language-selection-button"
					onClick={(e) => setLanguageSelectorButton(e.currentTarget)}
					src={selectedLanguage.iconUrl}
					sx={{
						width: 32,
						height: 32,
						outline: (theme) => `2px solid ${theme.palette.grey[200]}`,
						outlineOffset: 3,
						marginRight: 0.5,
						cursor: 'pointer'
					}}
				/>
				<Menu
					anchorEl={languageSelectorButton}
					open={!!languageSelectorButton}
					onClose={closeLanguageSelectionMenu}
					MenuListProps={{ dense: true }}
					PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}
					anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
					transformOrigin={{ horizontal: 'center', vertical: 'bottom' }}
				>
					{AVAILABLE_LANGUAGES.map(({ code, Icon, text }) => {
						return (
							<MenuItem
								key={`language-${code}`}
								divider
								onClick={() => onLanguageSelect(code)}
								selected={code === selectedLanguageCode}
							>
								<Stack direction="row" spacing={2} alignItems="center">
									<Box component={Icon} sx={{ width: 24 }} />
									<Typography>{text}</Typography>
								</Stack>
							</MenuItem>
						);
					})}
				</Menu>
			</Box>

			<GuardedRender canRender={(user) => user.isDealer(user.role) || user.isChargingPartner(user.role)}>
				<Tooltip
					title={<Tr.Portal path="common.user-actions.switch-role-tooltip" />}
					arrow
					componentsProps={{ tooltip: { sx: { padding: 2 } } }}
				>
					<OutlinedIconButton
						data-testid="switch-role-dialog-open-button"
						onClick={() => dispatch(setRoleSwitchDialogVisible(true))}
					>
						<OrganizationsIcon sx={{ color: 'inherit', width: 20, height: 20 }} />
					</OutlinedIconButton>
				</Tooltip>
			</GuardedRender>

			<Tooltip
				title={<Tr.Portal path="common.user-actions.configuration-tooltip" />}
				arrow
				componentsProps={{ tooltip: { sx: { padding: 2 } } }}
			>
				<OutlinedIconButton data-testid="settings-dialog-open-button" onClick={() => navigate(`${PortalRoutePath.CONFIGURATION}`)}>
					<SettingsIcon sx={{ color: 'inherit', width: 20, height: 20 }} />
				</OutlinedIconButton>
			</Tooltip>
			<Tooltip
				title={<Tr.Portal path="common.user-actions.help-tooltip" />}
				arrow
				componentsProps={{ tooltip: { sx: { padding: 2 } } }}
			>
				<OutlinedIconButton data-testid="settings-dialog-open-button" onClick={() => navigate(`${PortalRoutePath.HELP}`)}>
					<QuestionMarkIcon sx={{ color: 'inherit', width: 20, height: 20 }} />
				</OutlinedIconButton>
			</Tooltip>
			<Tooltip
				title={<Tr.Portal path="common.user-actions.logout-tooltip" />}
				arrow
				componentsProps={{ tooltip: { sx: { padding: 2 } } }}
			>
				<OutlinedIconButton
					data-testid="logout-button"
					onClick={() => {
						authService
							.logout()
							.then(() => navigate('/login'))
							.catch(RxUtils.noop);
					}}
				>
					<LogoutIcon sx={{ color: 'inherit', width: 20, height: 20 }} />
				</OutlinedIconButton>
			</Tooltip>
			<RoleSwitchDialog />
		</Stack>
	) : null;
};
